import jsPDF from 'jspdf';
import 'jspdf-autotable';
import formatPrice from 'utils/formatPrice';
import patientDobToAge from 'utils/patientDobToAge';
import sortInsurance from 'utils/sortInsurance';

const patientProfileToPDF = ({ inputPDF, patient }) => new Promise((resolve) => {
  const lineHeight = 1.2;
  const margin = 10;
  const ptsPerMm = 2.834646;
  const fontSize = 10;
  const titleFontSize = 18;
  const fontType = 'normal';
  const textColor = 0;
  const drawColor = 0;
  const lineHeightMm = fontSize * (lineHeight / ptsPerMm);
  const format = 'a4';
  // eslint-disable-next-line new-cap
  const doc = inputPDF || new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format,
    lineHeight,
  });
  if (inputPDF) {
    doc.addPage(format);
  }
  doc.setFont('times');
  let y = margin;
  let x = margin;
  const pageContentWidth = doc.internal.pageSize.getWidth() - (margin * 2);
  let maxWidth = pageContentWidth;
  const setDefaults = () => {
    doc.setFontSize(fontSize);
    doc.setFontType(fontType);
    doc.setTextColor(textColor);
    doc.setDrawColor(drawColor);
  };
  const setX = () => {
    x = margin;
  };
  const addY = (newY) => {
    if ((y + newY) < doc.internal.pageSize.getHeight() - (margin * 2)) {
      y += newY;
    } else {
      doc.addPage();
      y = margin;
    }
  };
  const renderTitle = () => {
    doc.setFontSize(titleFontSize);
    doc.setFontType('bold');
    x = (doc.internal.pageSize.getWidth() / 2);
    maxWidth = pageContentWidth * 0.75;
    const text = doc.splitTextToSize(`${patient['Last Name']}, ${patient['First Name']} Profile`, maxWidth);
    text.forEach((line) => {
      addY(titleFontSize * (lineHeight / ptsPerMm));
      doc.text(line, x, y, null, null, 'center');
    });
  };
  const renderPatientGeneral = () => {
    setDefaults();
    setX();
    maxWidth = (pageContentWidth / 2) - (margin / 2);
    const columns = [
      { title: 'Label', dataKey: 'label' },
      { title: 'Value', dataKey: 'value' },
    ];
    const rows = [
      ...[{
        label: 'Patient Name',
        value: `${patient['First Name']} ${patient['Middle Name'] || ''}, ${patient['Last Name']}`,
      },
      {
        label: patient['Date of Death']
          ? 'Date of Birth'
          : 'Date of Birth, Age',
        value: patient['Date of Death']
          ? patient['Date of Birth']
          : `${patient['Date of Birth']}, ${patientDobToAge(patient['Date of Birth'])}`,
      }],
      ...patient['Date of Death'] ? [{
        label: 'Date of Death',
        value: patient['Date of Death'],
      }] : [],
      ...[{
        label: 'Gender',
        value: patient.Gender,
      },
      {
        label: 'Social Security Number',
        value: patient['Social Security Number'] || '',
      },
      {
        label: 'Status',
        value: patient['Patient Status'] === true ? 'Active' : 'Inactive',
      }],
    ];
    const startY = y;
    addY((margin / 2) + lineHeightMm);
    doc.text('Personal Information', x, y);
    addY(margin / 4);
    doc.autoTable(columns, rows, {
      theme: 'grid',
      startY: y,
      showHeader: 'never',
      margin: [0, 0, 0, x],
      tableWidth: maxWidth,
      styles: {
        font: 'times',
        textColor,
        lineColor: drawColor,
        fontSize,
        cellPadding: [0, margin / 4, 0, margin / 4],
        columnWidth: 'auto',
        overflow: 'linebreak',
      },
      columnStyles: {
        label: {
          fontStyle: 'bold',
        },
      },
    });
    y = startY;
  };
  const renderPatientContactInformation = () => {
    setDefaults();
    x = (margin * 1.5) + (pageContentWidth / 2);
    maxWidth = (pageContentWidth / 2) - (margin / 2);
    const columns = [
      { title: 'Label', dataKey: 'label' },
      { title: 'Value', dataKey: 'value' },
    ];
    const addressInfo = patient['Address Information'];
    let address;
    if (addressInfo.Street) {
      address = addressInfo.Apt
        ? `${addressInfo.Street} ST. APT. ${addressInfo.Apt}, ${addressInfo.City}, ${addressInfo.State || ''} ${addressInfo['Zip Code']}`
        : `${addressInfo.Street} ST., ${addressInfo.City}, ${addressInfo.State || ''} ${addressInfo['Zip Code']}`;
    }
    const rows = [
      {
        label: 'Cell Phone',
        value: patient['Contact Information']['Primary Phone'] || '',
      },
      {
        label: 'Home Phone',
        value: patient['Contact Information']['Home Phone'] || '',
      },
      {
        label: 'Emergency Contact',
        value: patient['Contact Information']['Work Phone'] || '',
      },
      {
        label: 'Email',
        value: patient['Contact Information'].Email || '',
      },
      {
        label: 'Prefered Method of communication',
        value: patient['Contact Information']['Preferred method of communications'] || '',
      },
      {
        label: 'Address',
        value: address,
      },
    ];
    addY((margin / 2) + lineHeightMm);
    doc.text('Contact Information', x, y);
    addY(margin / 4);
    doc.autoTable(columns, rows, {
      theme: 'grid',
      startY: y,
      showHeader: 'never',
      margin: [0, 0, 0, x],
      tableWidth: maxWidth,
      styles: {
        font: 'times',
        textColor,
        lineColor: drawColor,
        fontSize,
        cellPadding: [0, margin / 4, 0, margin / 4],
        columnWidth: 'auto',
        overflow: 'linebreak',
      },
      columnStyles: {
        label: {
          fontStyle: 'bold',
        },
      },
    });
    const table = doc.autoTable.previous;
    addY(table.finalY - y);
  };
  const renderPatientInsurance = () => {
    const insurances = patient['Insurance Information'].filter(item =>
      item['Insurance Type'] !== 'Self');
    setDefaults();
    addY(lineHeightMm);
    x = margin;
    maxWidth = pageContentWidth;
    if (insurances.length > 0) {
      const columns = [
        { title: 'Payer', dataKey: 'payerName' },
        { title: 'Order of\n Benefits', dataKey: 'orderBenefits' },
        { title: 'Type', dataKey: 'type' },
        { title: 'Insurance ID, Group ID', dataKey: 'insuranceID' },
        { title: 'Effective From/To', dataKey: 'effectiveFromTo' },
        { title: 'Copay', dataKey: 'copay' },
        { title: 'Status', dataKey: 'status' },
        { title: 'Comments', dataKey: 'comments' },
      ];
      const rows = sortInsurance(insurances)
        .map((item) => {
          const effectiveDates = item['Effective From'] && item['Effective To']
            ? `${item['Effective From']} - ${item['Effective To']}` : '';
          let copay;
          switch (item['Copay Type']) {
            case 'Fixed':
              copay = formatPrice(item['Copay Amount']);
              break;
            case 'Percent':
              copay = `${item['Copay Amount']}%`;
              break;
            default:
              copay = '';
          }
          return {
            payerName: item['Insurance Info']['Payer Name'],
            orderBenefits: item['Order Benefits'],
            type: item['Claim Filling Code'] && item['Claim Filling Code']['Full Name']
              ? item['Claim Filling Code']['Full Name'] : '',
            insuranceID: item['Group ID'] ? `${item['Insurance ID']}, ${item['Group ID']}` : item['Insurance ID'],
            effectiveFromTo: effectiveDates,
            copay,
            status: item['Insurance Status'],
            comments: item.Comments ? item.Comments : '',
          };
        });
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
        columnStyles: {
          orderBenefits: {
            columnWidth: 22,
          },
          type: {
            columnWidth: 25,
          },
          insuranceID: {
            columnWidth: 27,
          },
          effectiveFromTo: {
            columnWidth: 24,
          },
          copay: {
            columnWidth: 'wrap',
          },
          status: {
            columnWidth: 'wrap',
          },
          comments: {
            columnWidth: 25,
          },
        },
      });
      const table = doc.autoTable.previous;
      addY((table.finalY - y) + margin);
    } else {
      doc.text('Patient has no Insurance', x, y);
      addY(margin);
    }
  };
  const renderPatientCreditCards = () => {
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (patient['Credit Card'].length > 0) {
      const columns = ['Card Number', 'Name on Card', 'Date of Expiration'];
      const rows = patient['Credit Card'].map(item => ([
        item['A default payment method']
          ? `${item['Card Number']}(Default)`
          : item['Card Number'],
        item['Name on Card'],
        item['Date of Expiration'],
      ]));
      doc.text('Credit Cards', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
      });
      const table = doc.autoTable.previous;
      addY(table.finalY - y);
    } else {
      doc.text('Patient has no Credit Cards', x, y);
    }
  };
  const renderPatientReferralSources = () => {
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (patient['Referral Source'].length > 0) {
      const columns = ['Name', 'Start date', 'Phone', 'Email'];
      const rows = patient['Referral Source'].map(item => ([
        item['Name of source'],
        item['Cooperate From'] ? item['Cooperate From'] : '',
        item['Referral Source Contact'] ? item['Referral Source Contact'].map(contactItem => contactItem.Phone).filter(phone => phone).join(', ') : '',
        item['Referral Source Contact'] ? item['Referral Source Contact'].map(contactItem => contactItem.Email).filter(email => email).join(', ') : '',
      ]));
      doc.text('Referral Sources', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
      });
      const table = doc.autoTable.previous;
      addY(table.finalY - y);
    } else {
      doc.text('Patient has no Referral Sources', x, y);
    }
  };
  const renderPatientSalesRepresentatives = () => {
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (patient['Sales Representative'].length > 0) {
      const columns = ['Name', 'Phone', 'Email'];
      const rows = patient['Sales Representative'].map(item => ([
        `${item['First Name']} ${item['Middle Name'] || ''}, ${item['Last Name']}`,
        item.Phone ? item.Phone : '',
        item.Email ? item.Email : '',
      ]));
      doc.text('Sales Representatives', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
      });
      const table = doc.autoTable.previous;
      addY(table.finalY - y);
    } else {
      doc.text('Patient has no Sales Representatives', x, y);
    }
  };
  const renderPatientPrescribers = () => {
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (patient.Prescriber.length > 0) {
      const columns = ['Name', 'Email', 'Phone', 'NPI', 'LIC', 'DEA'];
      const rows = patient.Prescriber.map(item => ([
        `${item['First Name']} ${item['Last Name']}`,
        item.Email ? item.Email : '',
        item.Phone ? item.Phone : '',
        item.NPI ? item.NPI : '',
        item.LIC ? item.LIC : '',
        item.DEA ? item.DEA : '',
      ]));
      doc.text('Prescribers', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
      });
      const table = doc.autoTable.previous;
      addY(table.finalY - y);
    } else {
      doc.text('Patient has no prescribers', x, y);
    }
  };
  const renderPatientVitals = () => {
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (patient.Vitals.length > 0) {
      const columns = ['Weight', 'Height', 'BMI', 'Date'];
      const rows = patient.Vitals.map(item => ([
        `${item.Weight} lb`,
        `${item['Height Ft']}ft, ${item['Height In']}in`,
        `${item.BMI}`,
        item['Create At'],
      ]));
      doc.text('Vitals', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'everyPage',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        headerStyles: {
          lineWidth: 0.1,
          lineColor: drawColor,
          fillColor: 'white',
        },
      });
      const table = doc.autoTable.previous;
      addY(table.finalY - y);
    } else {
      doc.text('Patient has no vitals', x, y);
    }
  };

  const renderPatientDiagnoses = () => {
    setDefaults();
    setX();
    maxWidth = pageContentWidth;
    addY((margin / 2) + lineHeightMm);
    if (patient.Prescriber.length > 0) {
      const columns = [
        { title: 'DX Code', dataKey: 'dxCode' },
        { title: 'Long Description', dataKey: 'longDescription' },
        { title: 'create_at', dataKey: 'createAt' },
      ];
      const rows = patient.Diagnosis.map(item => ({
        dxCode: item['DX Code'],
        longDescription: item['Long Description'],
        createAt: item.create_at,
      }));
      doc.text('Diagnoses', x, y);
      addY(margin / 4);
      doc.autoTable(columns, rows, {
        theme: 'grid',
        startY: y,
        showHeader: 'never',
        margin: [0, 0, 0, x],
        tableWidth: maxWidth,
        styles: {
          font: 'times',
          textColor,
          lineColor: drawColor,
          fontSize,
          cellPadding: [0, margin / 4, 0, margin / 4],
          columnWidth: 'auto',
          overflow: 'linebreak',
        },
        columnStyles: {
          dxCode: {
            columnWidth: 'wrap',
            fontStyle: 'bold',
          },
          createAt: {
            columnWidth: 'wrap',
          },
        },
      });
    } else {
      doc.text('Patient has no diagnoses', x, y);
    }
  };

  renderTitle();
  renderPatientGeneral();
  renderPatientContactInformation();
  renderPatientInsurance();
  renderPatientCreditCards();
  renderPatientReferralSources();
  renderPatientSalesRepresentatives();
  renderPatientPrescribers();
  renderPatientVitals();
  renderPatientDiagnoses();
  resolve(doc);
});

export default patientProfileToPDF;



// WEBPACK FOOTER //
// ./src/utils/patientProfileToPDF.js